import { useState } from "react";
import { trpc } from "../lib/trpc";
import { Icon } from "../components/ui/Icon";
import { OrgTab } from "../components/hr/tabs/OrgTab";
import { PositionsTab } from "../components/hr/tabs/PositionsTab";
import { EmployeesTab } from "../components/hr/tabs/EmployeesTab";
import { LeaveTab } from "../components/hr/tabs/LeaveTab";
import { ShiftPlanTab } from "../components/hr/tabs/ShiftPlanTab";
import { AttendanceTab } from "../components/hr/tabs/AttendanceTab";
import { PerformanceTab } from "../components/hr/tabs/PerformanceTab";
import { RecruitmentTab } from "../components/hr/tabs/RecruitmentTab";
import { PayrollTab } from "../components/hr/tabs/PayrollTab";
import "./Items.css";
import "./Personnel.css";

type TabKey = "org" | "positions" | "employees" | "leave" | "shiftplan" | "attendance" | "performance" | "recruitment" | "payroll";

interface TabDef { key: TabKey; label: string; icon: string; show: boolean }

/**
 * Personel / İnsan Kaynakları — sekmeli hub (İK Faz 1-10).
 * Sekmeler yetkiye göre görünür: Organizasyon (view:org), İK sekmeleri (view:hr),
 * Bordro yalnız süper-admin. Yazma aksiyonları manage:org/manage:hr ile açılır.
 */
export default function PersonnelPage() {
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const perms = meQ.data?.user?.permissions ?? [];
  const isSuper = !!meQ.data?.user?.isSuperAdmin;
  const has = (p: string) => isSuper || perms.includes(p);
  const canManageHr = has("manage:hr");
  const canViewHr = has("view:hr");
  const canViewOrg = has("view:org");
  // Vardiya planı BİLİNÇLİ olarak view:hr'den ayrı: üretim şefi ızgarayı görmeli ama maaş/TC'yi görmemeli.
  const canViewShiftPlan = has("view:shift_plan");
  const canManageShiftPlan = has("manage:shift_plan");

  const tabs: TabDef[] = [
    { key: "org", label: "Organizasyon", icon: "building", show: canViewOrg },
    { key: "positions", label: "Pozisyonlar", icon: "columns", show: canViewHr },
    { key: "employees", label: "Personeller", icon: "users", show: canViewHr },
    { key: "leave", label: "İzinler", icon: "calendar", show: canViewHr },
    { key: "shiftplan", label: "Vardiya Planı", icon: "shuffle", show: canViewShiftPlan },
    { key: "attendance", label: "Puantaj", icon: "clock", show: canViewHr },
    { key: "performance", label: "Performans", icon: "chart", show: canViewHr },
    { key: "recruitment", label: "İşe Alım", icon: "inbox", show: canViewHr },
    { key: "payroll", label: "Bordro", icon: "fileText", show: isSuper },
  ];
  const visible = tabs.filter((t) => t.show);
  // VARSAYILAN SEKME: Vardiya Planı — modülün günlük olarak en çok kullanılan ekranı.
  // Organizasyon şeması bir KURULUM ekranı; bir kez yapılır, her gün açılmaz.
  // Yetkisi olmayan kullanıcıda aşağıdaki geri düşme ilk görünür sekmeye alır.
  const [tab, setTab] = useState<TabKey>("shiftplan");
  // Aktif sekme görünür değilse ilk görünür sekmeye düş.
  const active = visible.some((t) => t.key === tab) ? tab : visible[0]?.key ?? "org";

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Personel / İnsan Kaynakları</h1>
          <p className="page-sub">Organizasyon, pozisyon, personel, izin, vardiya planı, puantaj, bordro ve işe alım yönetimi.</p>
        </div>
      </div>

      {meQ.isLoading ? (
        <p className="td-empty">Yükleniyor…</p>
      ) : visible.length === 0 ? (
        <div className="d-card"><div className="d-card-body"><p className="td-empty">Bu modül için yetkiniz bulunmuyor.</p></div></div>
      ) : (
        <div className="dt2-wrap">
          <div className="dt2-tabs" role="tablist">
            {visible.map((t) => (
              <button key={t.key} type="button" role="tab" aria-selected={active === t.key}
                className={`dt2-tab ${active === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
                <Icon name={t.icon} size={14} />{t.label}
              </button>
            ))}
          </div>
          <div className="hr-tab-content">
            {active === "org" ? <OrgTab /> : null}
            {active === "positions" ? <PositionsTab canManage={canManageHr} isSuper={isSuper} /> : null}
            {active === "employees" ? <EmployeesTab canManage={canManageHr} isSuper={isSuper} /> : null}
            {active === "leave" ? <LeaveTab canManage={canManageHr} isSuper={isSuper} /> : null}
            {active === "shiftplan" ? <ShiftPlanTab canManage={canManageShiftPlan} isSuper={isSuper} /> : null}
            {active === "attendance" ? <AttendanceTab canManage={canManageHr} isSuper={isSuper} /> : null}
            {active === "performance" ? <PerformanceTab canManage={canManageHr} isSuper={isSuper} /> : null}
            {active === "recruitment" ? <RecruitmentTab canManage={canManageHr} isSuper={isSuper} /> : null}
            {active === "payroll" ? <PayrollTab canManage={canManageHr} isSuper={isSuper} /> : null}
          </div>
        </div>
      )}
    </div>
  );
}
